/* Customer ETA Portal — public no-login page for a customer.
   URL: /track/:token — mobile-first live tracker of today's deliveries. */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Truck, MapPin, Clock, HardHat, RefreshCcw, CheckCircle2 } from "lucide-react";
import GadtechLogo from "@/components/GadtechLogo";
import useLiveRefresh from "@/hooks/useLiveRefresh";

import { BACKEND_ORIGIN } from "@/lib/api";
const BACKEND = BACKEND_ORIGIN;
const API = `${BACKEND}/api`;

const statusColor = (s) => {
  const m = {
    scheduled: "text-sky-500 border-sky-500/40 bg-sky-500/10",
    in_progress: "text-primary-orange border-primary-orange/50 bg-primary-orange/10",
    completed: "text-emerald-500 border-emerald-500/40 bg-emerald-500/10",
  };
  return m[s] || "text-muted-industrial border-industrial";
};

const truckStatusLabel = (s) => ({
  available: "At plant",
  loading: "Loading",
  en_route: "En route",
  on_site: "On site",
  returning: "Returning",
  maintenance: "Service",
}[s] || s || "—");

export default function CustomerTracker() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [now, setNow] = useState(new Date());
  const isDemo = !!data?.supplier?.is_demo;
  useLiveRefresh(() => load(), { intervalMs: isDemo ? 5000 : 30_000, org: isDemo ? data?.supplier?.org_id : null });

  const load = async () => {
    try {
      const r = await axios.get(`${API}/public/customer/${token}`);
      setData(r.data);
      setError(null);
    } catch (e) {
      setError(e.response?.status === 404 ? "Invalid tracking link" : "Cannot reach server");
    }
  };

  useEffect(() => {
    load();
    const c = setInterval(() => setNow(new Date()), 5000);
    return () => { clearInterval(c); };
    // eslint-disable-next-line
  }, [token]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-[rgb(var(--bg))]" data-testid="tracker-error">
        <div className="max-w-md text-center">
          <div className="w-14 h-14 rounded-xl bg-red-500/20 border border-red-500/40 flex items-center justify-center mx-auto mb-4">
            <HardHat className="w-6 h-6 text-red-500" />
          </div>
          <h1 className="heading text-2xl font-black uppercase mb-2">{error}</h1>
          <p className="text-muted-industrial text-sm">Please contact your supplier for a fresh tracking link.</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return <div className="min-h-screen flex items-center justify-center bg-[rgb(var(--bg))] label-tag text-primary-orange pulse-orange">Connecting…</div>;
  }

  return (
    <div className="min-h-screen bg-[rgb(var(--bg))] pb-14" data-testid="customer-tracker">
      {/* Header */}
      <div className="bg-surface border-b-2 border-primary-orange sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <GadtechLogo className="h-10" data-testid="tracker-logo" />
            <div className="label-tag text-primary-orange">LIVE TRACKING</div>
          </div>
          <button onClick={load} className="mono text-[10px] uppercase tracking-widest text-muted-industrial hover:text-primary-orange inline-flex items-center gap-1" data-testid="tracker-refresh">
            <RefreshCcw className="w-3 h-3" /> Refresh
          </button>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-6 space-y-4">
        <div>
          <div className="label-tag">CUSTOMER</div>
          <h1 className="heading text-2xl font-black uppercase tracking-tight">{data.customer.name}</h1>
          <div className="text-xs text-muted-industrial mt-1">Deliveries for {data.date} · Updated {new Date(data.server_time).toLocaleTimeString()}</div>
        </div>

        {data.jobs.length === 0 && (
          <div className="card-v2 p-8 text-center" data-testid="no-deliveries">
            <Truck className="w-8 h-8 text-muted-industrial mx-auto mb-3" />
            <div className="heading text-lg font-bold uppercase">No deliveries today</div>
            <div className="text-sm text-muted-industrial mt-1">Check back later, or contact your supplier to book a load.</div>
          </div>
        )}

        {data.jobs.map((j) => (
          <div key={j.job_id} data-testid={`tracker-job-${j.job_id}`} className="card-v2 p-4">
            <div className="flex items-start justify-between gap-3 mb-3">
              <div className="min-w-0">
                <div className="mono text-[10px] uppercase tracking-widest text-primary-orange">{j.time_required || "TBA"}</div>
                <div className="heading font-black text-lg truncate">{j.site_name || "Delivery"}</div>
                <div className="text-xs text-muted-industrial truncate mt-0.5">
                  <MapPin className="w-3 h-3 inline mr-1" />
                  {j.site_address || "—"}
                </div>
              </div>
              <span className={`mono text-[10px] uppercase tracking-widest px-2.5 py-1 border rounded-md ${statusColor(j.status)} shrink-0`}>
                {j.status?.replace("_", " ")}
              </span>
            </div>

            <div className="grid grid-cols-3 gap-3 text-xs border-t border-industrial pt-3">
              <div><div className="label-tag">Mix</div><div className="mono">{j.mix_code}</div></div>
              <div><div className="label-tag">Ordered</div><div className="mono">{j.quantity_m3} m³</div></div>
              <div><div className="label-tag">Delivered</div><div className="mono text-primary-orange">{j.delivered_m3} / {j.quantity_m3} m³</div></div>
            </div>

            {/* Progress bar */}
            <div className="mt-3 h-2 bg-surface-2 rounded-full overflow-hidden">
              <div className="h-full bg-primary-orange transition-all"
                   style={{ width: `${Math.min(100, (j.delivered_m3 / Math.max(1, j.quantity_m3)) * 100)}%` }} />
            </div>

            {/* Trucks */}
            {j.trucks.length > 0 ? (
              <div className="mt-4 space-y-2">
                <div className="label-tag">Trucks en route</div>
                {j.trucks.map((t) => (
                  <div key={t.truck_id} className="border border-industrial rounded-lg p-3 flex items-center justify-between gap-2" data-testid={`tracker-truck-${t.number}`}>
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-9 h-9 rounded-lg bg-primary-orange/15 border border-primary-orange/30 flex items-center justify-center shrink-0">
                        <Truck className="w-4 h-4 text-primary-orange" />
                      </div>
                      <div className="min-w-0">
                        <div className="heading font-black text-sm">{t.number}</div>
                        <div className="text-[11px] mono text-muted-industrial">{truckStatusLabel(t.status)}</div>
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      {t.eta_min != null ? (
                        <>
                          <div className="mono text-lg font-bold text-primary-orange leading-none inline-flex items-center gap-1">
                            <Clock className="w-3.5 h-3.5" /> {t.eta_min} min
                          </div>
                          <div className="text-[10px] mono text-muted-industrial mt-1">{t.distance_km} km away</div>
                        </>
                      ) : t.status === "on_site" ? (
                        <div className="mono text-sm font-bold text-emerald-500 inline-flex items-center gap-1">
                          <CheckCircle2 className="w-4 h-4" /> On site
                        </div>
                      ) : (
                        <div className="text-[11px] mono text-muted-industrial">Locating…</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : j.status !== "completed" && (
              <div className="mt-4 text-xs text-muted-industrial mono border border-dashed border-industrial rounded-lg p-3 text-center">
                Truck not yet assigned — we'll show ETA here once dispatched.
              </div>
            )}
          </div>
        ))}

        <div className="text-center pt-6 label-tag">© ARMORBATCH GLOBAL · Live Tracker</div>
      </div>
    </div>
  );
}
